import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { IconCategory, IconType } from '@deepguide-ai/types'
import { ChevronDown, Search, Shuffle, SmilePlus, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@deepguide-ai/ui'
import { IconComponent } from '../icon-component'
import { getIconStyleClasses } from '../icon-component/utils'
import {
  searchEmojis,
  getEmojisByCategory,
  getEmojiCategories,
  getCategoryIcon,
  getEmojiCharacter,
  EmojiMartEmoji,
  EmojiMartCategory,
} from './emoji-data'
import { AVAILABLE_ICONS } from './icon-data'

// Note: IconType is imported from database enums and includes:
// REGULAR, SOLID, LIGHT, THIN, SHARP_SOLID, DUOTONE, BRANDS

// Color options for icons
const COLOR_OPTIONS = [
  '#6B7280', // gray
  '#EA580C', // orange
  '#DC2626', // red
  '#DB2777', // pink
  '#7C3AED', // purple
  '#2563EB', // blue
  '#059669', // green
  '#65A30D', // lime
]

export interface EmojiPickerProps {
  selectedIcon?: string | null
  iconCategory?: IconCategory
  iconType?: IconType
  iconColor?: string
  isPopoverMode?: boolean
  onIconChange?: (
    iconName: string,
    iconCategory: IconCategory,
    iconType?: IconType,
    iconColor?: string
  ) => void
  onColorChange?: (color: string) => void
  onEmojiSelect?: (emoji: string) => void
  // 支持颜色选择
  showColor?: boolean
  portalled?: boolean
}

export function EmojiPicker({
  selectedIcon,
  iconCategory = 'LUCIDE',
  iconType = 'REGULAR',
  iconColor = '#000000',
  onIconChange,
  onColorChange,
  onEmojiSelect,
  isPopoverMode = false,
  showColor = true,
  portalled = true,
}: EmojiPickerProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'emoji' | 'icon'>(
    iconCategory === 'EMOJI' ? 'emoji' : 'icon'
  )
  const [selectedColor, setSelectedColor] = useState(iconColor)
  const [selectedEmojiCategory, setSelectedEmojiCategory] = useState('people') // Default to first emoji-mart category
  const [searchResults, setSearchResults] = useState<EmojiMartEmoji[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [categoryEmojis, setCategoryEmojis] = useState<EmojiMartEmoji[]>([])
  const [emojiCategories, setEmojiCategories] = useState<EmojiMartCategory[]>(
    []
  )
  const [isRandomizing, setIsRandomizing] = useState(false)
  const parentRef = useRef<HTMLDivElement>(null)
  const emojiParentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (iconColor !== selectedColor) {
      setSelectedColor(iconColor)
    }
  }, [iconColor])

  // Load emoji categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categories = await getEmojiCategories()
        setEmojiCategories(categories)
        // Set the first category as default if not already set
        if (categories.length > 0 && selectedEmojiCategory === 'people') {
          setSelectedEmojiCategory(categories[0].id)
        }
      } catch (error) {
        console.error('Error loading emoji categories:', error)
      }
    }

    loadCategories()
  }, [])

  // Handle emoji search using emoji-mart data
  useEffect(() => {
    const handleEmojiSearch = async () => {
      if (activeTab !== 'emoji' || !searchQuery.trim()) {
        setSearchResults([])
        setIsSearching(false)
        return
      }

      setIsSearching(true)
      try {
        const results = await searchEmojis(searchQuery)
        setSearchResults(results)
      } catch (error) {
        console.error('Error searching emojis:', error)
        setSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }

    // Set searching state immediately when user starts typing
    if (activeTab === 'emoji' && searchQuery.trim()) {
      setIsSearching(true)
    }

    const timeoutId = setTimeout(handleEmojiSearch, 300) // Debounce search
    return () => clearTimeout(timeoutId)
  }, [searchQuery, activeTab])

  // Load emojis for selected category
  useEffect(() => {
    const loadCategoryEmojis = async () => {
      if (activeTab !== 'emoji' || searchQuery.trim()) return

      try {
        const emojis = await getEmojisByCategory(selectedEmojiCategory)
        setCategoryEmojis(emojis)
      } catch (error) {
        console.error('Error loading category emojis:', error)
        // Clear emojis on error
        setCategoryEmojis([])
      }
    }

    loadCategoryEmojis()
  }, [selectedEmojiCategory, activeTab, searchQuery])

  // Filter icons based on search query
  const filteredIcons = React.useMemo(() => {
    if (!searchQuery.trim()) return AVAILABLE_ICONS

    const query = searchQuery.toLowerCase()
    return AVAILABLE_ICONS.filter(
      (iconData) =>
        iconData.name.toLowerCase().includes(query) ||
        iconData.keywords.some((keyword) =>
          keyword.toLowerCase().includes(query)
        )
    )
  }, [searchQuery])

  const iconStyleClasses = getIconStyleClasses(iconType)

  // Virtual list for icons
  const rowVirtualizer = useVirtualizer({
    count: Math.ceil(filteredIcons.length / 9),
    getScrollElement: () => parentRef.current,
    estimateSize: () => 40,
    overscan: 10,
  })

  // Get emojis to display (either search results or category emojis)
  const emojisToDisplay = React.useMemo(() => {
    if (searchQuery.trim()) {
      return searchResults
    }
    return categoryEmojis
  }, [searchQuery, searchResults, categoryEmojis])

  // Virtual list for emojis
  const emojiVirtualizer = useVirtualizer({
    count: Math.ceil(emojisToDisplay.length / 9),
    getScrollElement: () => emojiParentRef.current,
    estimateSize: () => 40,
    overscan: 10,
  })

  // Re-measure virtualizers after the Popover becomes visible.
  // We defer the measurement to the next animation frame to make sure
  // the Popover has been laid out and has non-zero dimensions.
  useEffect(() => {
    if (!isOpen) return

    const rafId = requestAnimationFrame(() => {
      rowVirtualizer.measure()
      emojiVirtualizer.measure()
    })

    return () => cancelAnimationFrame(rafId)
  }, [isOpen, rowVirtualizer, emojiVirtualizer])

  const handleIconSelect = useCallback(
    (iconName: string) => {
      onIconChange?.(iconName, 'LUCIDE', iconType, selectedColor)
      setIsOpen(false)
    },
    [onIconChange, iconType, selectedColor]
  )

  const handleEmojiSelect = useCallback(
    (emoji: string) => {
      onEmojiSelect?.(emoji)
      setIsOpen(false)
    },
    [onEmojiSelect]
  )

  const handleColorSelect = useCallback(
    (color: string) => {
      setSelectedColor(color)
      onColorChange?.(color)
    },
    [onColorChange]
  )

  const handleDelete = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    e.preventDefault()
    onIconChange?.('', 'LUCIDE')
  }

  const handleRandomSelection = useCallback(async () => {
    // Check if there are items to select from
    const hasItems =
      activeTab === 'emoji'
        ? searchQuery.trim()
          ? searchResults.length > 0
          : categoryEmojis.length > 0
        : filteredIcons.length > 0

    if (!hasItems) {
      return // Don't animate if there's nothing to select
    }

    setIsRandomizing(true)

    // Add a small delay for visual feedback
    setTimeout(() => {
      if (activeTab === 'emoji') {
        // Random emoji selection
        if (searchQuery.trim()) {
          // If searching, pick from search results
          if (searchResults.length > 0) {
            const randomEmoji =
              searchResults[Math.floor(Math.random() * searchResults.length)]
            const emojiChar = getEmojiCharacter(randomEmoji)
            handleEmojiSelect(emojiChar)
          }
        } else {
          // Pick from current category
          if (categoryEmojis.length > 0) {
            const randomEmoji =
              categoryEmojis[Math.floor(Math.random() * categoryEmojis.length)]
            const emojiChar = getEmojiCharacter(randomEmoji)
            handleEmojiSelect(emojiChar)
          }
        }
      } else {
        // Random icon selection
        if (filteredIcons.length > 0) {
          const randomIcon =
            filteredIcons[Math.floor(Math.random() * filteredIcons.length)]
          handleIconSelect(randomIcon.name)
        }
      }
      setIsRandomizing(false)
    }, 150)
  }, [
    activeTab,
    searchQuery,
    searchResults,
    categoryEmojis,
    filteredIcons,
    handleEmojiSelect,
    handleIconSelect,
  ])
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={isPopoverMode ? 'outline' : 'ghost'}
          className={`${isPopoverMode ? 'size-9 items-center justify-center !px-0 [&_svg]:size-4' : '-ml-1.5 !w-9 items-center'}`}
        >
          {selectedIcon ? (
            <>
              <div className='flex w-full items-center justify-center gap-2'>
                <IconComponent
                  size={isPopoverMode ? 16 : 31}
                  iconName={selectedIcon}
                  iconCategory={iconCategory}
                  style={{ color: selectedColor }}
                  className={cn(
                    'flex-shrink-0 transition-colors',
                    iconStyleClasses,
                    !isPopoverMode && '[&_svg]:size-[31px]'
                  )}
                />
                {/* {isPopoverMode && iconCategory === 'LUCIDE' && selectedIcon} */}
              </div>
              {/* {isPopoverMode && (
                <div
                  onClick={(e) => handleDelete(e)}
                  className='hover:bg-accent text-muted-foreground cursor flex size-5 items-center justify-center rounded-full'
                >
                  <X size={16} />
                </div>
              )} */}
            </>
          ) : (
            <>
              {isPopoverMode ? (
                <>
                  <SmilePlus size={16} />
                </>
              ) : (
                <IconComponent
                  size={31}
                  strokeWidth={2}
                  iconName={'file'}
                  style={{ color: 'var(--color-muted-foreground)' }}
                  className={cn(
                    'flex-shrink-0 transition-colors',
                    iconStyleClasses,
                    '[&_svg]:size-[31px]'
                  )}
                />
              )}
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-96 p-0' align='start' portalled={portalled}>
        <Tabs
          defaultValue={activeTab}
          onValueChange={(v) => setActiveTab(v as 'emoji' | 'icon')}
        >
          <TabsList className='text-foreground h-[42px] w-full justify-start gap-4 rounded-none border-b bg-transparent pl-5'>
            <TabsTrigger
              value='emoji'
              onClick={() => {
                if (emojiCategories.length > 0) {
                  setSelectedEmojiCategory(emojiCategories[0].id)
                }
              }}
              className='hover:text-foreground data-[state=active]:after:bg-primary relative w-fit !flex-none after:absolute after:inset-x-0 after:bottom-0 after:-mb-[7px] after:h-0.5 data-[state=active]:bg-transparent data-[state=active]:shadow-none'
            >
              Emoji
            </TabsTrigger>
            <TabsTrigger
              value='icon'
              className='hover:text-foreground data-[state=active]:after:bg-primary relative w-fit !flex-none after:absolute after:inset-x-0 after:bottom-0 after:-mb-[7px] after:h-0.5 data-[state=active]:bg-transparent data-[state=active]:shadow-none'
            >
              Icons
            </TabsTrigger>
          </TabsList>

          <div className='border-b px-3 pt-2'>
            <div className='mb-4 flex items-center justify-between gap-2'>
              <div className='relative flex-1'>
                <Search
                  size={16}
                  className='text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2 transform'
                />
                <Input
                  placeholder={
                    activeTab === 'emoji'
                      ? 'Search emojis...'
                      : 'Filter icons...'
                  }
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className='pl-9'
                />
              </div>
              <Button
                variant='outline'
                size='smIcon'
                onClick={handleRandomSelection}
                disabled={
                  isRandomizing ||
                  (activeTab === 'emoji'
                    ? searchQuery.trim()
                      ? searchResults.length === 0
                      : categoryEmojis.length === 0
                    : filteredIcons.length === 0)
                }
                title={`Random ${activeTab === 'emoji' ? 'emoji' : 'icon'}`}
                className={cn(isRandomizing && 'animate-pulse')}
              >
                <Shuffle
                  size={14}
                  className={cn(isRandomizing && 'animate-spin')}
                />
              </Button>
            </div>

            {activeTab === 'emoji' && !searchQuery.trim() && (
              <div className='text-muted-foreground mb-3 flex w-full items-center justify-between overflow-x-auto'>
                {emojiCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedEmojiCategory(category.id)}
                    className={cn(
                      'flex h-8 w-8 flex-shrink-0 cursor-pointer items-center justify-center rounded-full text-lg transition-colors',
                      selectedEmojiCategory === category.id
                        ? 'bg-accent'
                        : 'hover:bg-accent/50'
                    )}
                    title={category.name}
                  >
                    {getCategoryIcon(category.id)}
                  </button>
                ))}
              </div>
            )}

            {activeTab === 'icon' && showColor && (
              <div className='mb-3.5 flex items-center justify-between'>
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color}
                    onClick={() => handleColorSelect(color)}
                    className={cn(
                      'h-6 w-6 cursor-pointer rounded-full transition-all duration-200',
                      selectedColor === color
                        ? 'scale-110 ring-2 ring-offset-2'
                        : 'hover:scale-105'
                    )}
                    style={
                      {
                        backgroundColor: color,
                        ...(selectedColor === color
                          ? { '--tw-ring-color': color }
                          : {}),
                      } as React.CSSProperties
                    }
                  />
                ))}
              </div>
            )}
          </div>

          <TabsContent value='emoji' className='-mt-2'>
            <ScrollArea ref={emojiParentRef} type="always" className="h-60" viewClassName="py-2 [&>div]:!block">
              {isSearching ? (
                <div className='text-muted-foreground flex h-full items-center justify-center'>
                  <div className='text-center'>
                    <div className='border-border border-t-foreground mx-auto mb-2 size-6 animate-spin rounded-full border-2' />
                    <div className='text-sm'>Searching emojis...</div>
                  </div>
                </div>
              ) : emojisToDisplay.length === 0 && searchQuery.trim() ? (
                <div className='text-muted-foreground flex h-full items-center justify-center'>
                  <div className='text-center'>
                    <div className='mb-2 text-2xl'>😕</div>
                    <div className='text-sm'>No emojis found</div>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    height: `${emojiVirtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  {emojiVirtualizer.getVirtualItems().map((virtualRow) => {
                    const rowStart = virtualRow.index * 9
                    const rowEmojis = emojisToDisplay.slice(
                      rowStart,
                      rowStart + 9
                    )

                    return (
                      <div
                        key={virtualRow.index}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: `${virtualRow.size}px`,
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                        className='grid grid-cols-9 gap-1 px-2'
                      >
                        {rowEmojis.map((emoji, index) => {
                          const emojiChar = getEmojiCharacter(emoji)
                          return (
                            <Button
                              key={`${virtualRow.index}-${index}`}
                              variant='ghost'
                              onClick={() => handleEmojiSelect(emojiChar)}
                              className='flex h-10 w-10 items-center justify-center rounded-md text-xl transition-colors'
                              title={emoji.name}
                            >
                              <span role='img' aria-label={emoji.name}>
                                {emojiChar}
                              </span>
                            </Button>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          <TabsContent value='icon' className='-mt-2'>
            <ScrollArea
              ref={parentRef}
              className={`${showColor ? 'h-60' : 'h-71'}`}
              type="hover"
              viewClassName="[&>div]:!block py-2"
            >
              <div
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const rowStart = virtualRow.index * 9
                  const rowIcons = filteredIcons.slice(rowStart, rowStart + 9)

                  return (
                    <div
                      key={virtualRow.index}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: `${virtualRow.size}px`,
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                      className='grid grid-cols-9 gap-1 px-2'
                    >
                      {rowIcons.map((iconData) => {
                        return (
                          <Button
                            key={iconData.name}
                            variant='ghost'
                            onClick={() => handleIconSelect(iconData.name)}
                            className={cn(
                              'text-muted-foreground flex h-10 w-10 items-center justify-center rounded-md !p-1.5 transition-colors',
                              iconData.name === selectedIcon && 'bg-accent'
                            )}
                            title={iconData.name}
                          >
                            <IconComponent
                              size={20}
                              iconName={iconData.name}
                              style={{ color: selectedColor }}
                              className={cn(
                                'flex-shrink-0 transition-colors [&_svg]:size-5',
                                iconStyleClasses
                              )}
                            />
                          </Button>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  )
}
